import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Download,
  FileClock,
  RefreshCw,
  Search,
  TimerReset,
} from 'lucide-react';
import api from '../../apiPm';
import { getSafeApiErrorMessage, withTimeout } from '../PmShared/projectUtils';
import './PmTimesheet.css';

const PERIOD_LABELS = {
  this_week: 'This_Week',
  today: 'Today',
  this_month: 'This_Month',
  all: 'All_Time',
};

const EMPTY_DATA = {
  entries: [],
  summary: {
    hours_today: 0,
    weekly_hours: 0,
    pending_count: 0,
    approved_count: 0,
  },
};

const periodOptions = [
  { value: 'this_week', label: 'This week' },
  { value: 'today', label: 'Today' },
  { value: 'this_month', label: 'This month' },
  { value: 'all', label: 'All time' },
];

const sourceOptions = [
  { value: '', label: 'All sources' },
  { value: 'realtime', label: 'Realtime' },
  { value: 'manual', label: 'Manual' },
];

const DEFAULT_FILTERS = { search: '', userId: '', period: 'all', source: '' };

const toNumber = (value) => {
  const n = Number(value || 0);
  return Number.isFinite(n) ? n : 0;
};

const formatHours = (value) => {
  const hours = toNumber(value);
  if (hours === 0) return '0h';
  return `${Number.isInteger(hours) ? hours : hours.toFixed(2)}h`;
};

const formatDuration = (seconds) => {
  const total = Math.max(0, Math.floor(toNumber(seconds)));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m`;
  return `${total}s`;
};

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
};

const SummaryCard = ({ icon: Icon, label, value, helper, tone, isLoading }) => (
  <div className="col-12 col-md-6 col-xl-3">
    <div className="card h-100">
      <div className="card-body d-flex align-items-center gap-3">
        <span className={`pm-timesheet-icon pm-timesheet-${tone}`}>
          <Icon size={20} />
        </span>
        <div style={{ minWidth: 0 }}>
          <div className="fw-bold">{isLoading ? <span className="placeholder col-5" /> : value}</div>
          <div className="text-muted pm-timesheet-helper">{label}</div>
          <div className="text-muted pm-timesheet-micro">{helper}</div>
        </div>
      </div>
    </div>
  </div>
);

const EmptyTimesheetState = ({ message = 'No timesheet entries match the current filters.' }) => (
  <div className="pm-timesheet-empty text-muted">
    <Clock size={44} className="mb-2 opacity-25" />
    <div className="fw-semibold">No timesheet entries</div>
    <div className="pm-timesheet-helper">{message}</div>
  </div>
);

const PmTimesheet = () => {
  const [data, setData] = useState(EMPTY_DATA);
  const [members, setMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  const setFilter = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));

  const fetchTimesheet = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage('');
    try {
      const params = {
        search: filters.search || undefined,
        // Backend controller reads `user_id` (snake_case), not `userId` —
        // see app/controllers/pm/pm.go Timesheets(). Sending `userId` here
        // made the Member filter a silent no-op (parseInt64Query never
        // found the param, so userID stayed nil and every row matched).
        user_id: filters.userId || undefined,
        period: filters.period === 'all' ? undefined : filters.period,
        status: filters.source || undefined,
      };
      const response = await withTimeout(api.get('/v1/pm/timesheets', { params }));
      const payload = response.data || {};
      setData({
        entries: Array.isArray(payload.entries) ? payload.entries : [],
        summary: { ...EMPTY_DATA.summary, ...(payload.summary || {}) },
      });
    } catch (error) {
      setData(EMPTY_DATA);
      setErrorMessage(getSafeApiErrorMessage(error, 'Unable to load timesheet work logs.'));
    } finally {
      setIsLoading(false);
    }
  }, [filters.search, filters.userId, filters.period, filters.source]);

  useEffect(() => {
    fetchTimesheet();
  }, [fetchTimesheet]);

  useEffect(() => {
    const loadMembers = async () => {
      try {
        const response = await withTimeout(api.get('/v1/pm/gantt/members'));
        setMembers(Array.isArray(response.data) ? response.data : []);
      } catch (_) {
        setMembers([]);
      }
    };
    loadMembers();
  }, []);

  const summaryCards = useMemo(() => [
    {
      label: 'Hours Today',
      value: formatHours(data.summary.hours_today),
      helper: 'Logged today',
      icon: Clock,
      tone: 'primary',
    },
    {
      label: 'Weekly Hours',
      value: formatHours(data.summary.weekly_hours),
      helper: 'Clocked and manual logs',
      icon: TimerReset,
      tone: 'info',
    },
    {
      label: 'Active Sessions',
      value: data.summary.pending_count || 0,
      helper: 'Clocked in right now',
      icon: FileClock,
      tone: 'warning',
    },
    {
      label: 'Closed Logs',
      value: data.summary.approved_count || 0,
      helper: 'Completed work sessions',
      icon: CheckCircle2,
      tone: 'success',
    },
  ], [data.summary]);

  const totalFilteredHours = useMemo(() => (
    data.entries.reduce((sum, row) => sum + toNumber(row.hours), 0)
  ), [data.entries]);

  const clearFilters = () => setFilters(DEFAULT_FILTERS);

  // Exports exactly the rows currently shown in the table — i.e. already
  // scoped by whatever search/member/period/source filters are active —
  // not a separate unfiltered fetch. Purely client-side (no backend call):
  // `data.entries` is already loaded, xlsx/file-saver are already app deps
  // (see SalesReport/utils/exportUtils.js for the same json_to_sheet ->
  // writeFile pattern this mirrors).
  const handleExportExcel = () => {
    const rows = data.entries.map((entry) => ({
      Date: formatDate(entry.work_date || entry.started_at),
      Project: entry.project_title || '-',
      Member: entry.member_name || '-',
      Task: entry.task_title || '-',
      'Subtask Of': entry.task_type === 'subtask' ? (entry.parent_task_title || '-') : '',
      Start: formatTime(entry.started_at),
      End: entry.ended_at ? formatTime(entry.ended_at) : 'Active',
      'Duration (Hours)': Math.round((toNumber(entry.duration_seconds) / 3600) * 100) / 100,
      Source: entry.source === 'manual' ? 'Manual' : 'Realtime',
      Note: entry.description || '',
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = [
      { wch: 14 }, // Date
      { wch: 28 }, // Project
      { wch: 20 }, // Member
      { wch: 28 }, // Task
      { wch: 24 }, // Subtask Of
      { wch: 10 }, // Start
      { wch: 10 }, // End
      { wch: 16 }, // Duration (Hours)
      { wch: 12 }, // Source
      { wch: 30 }, // Note
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Timesheet');

    const periodLabel = PERIOD_LABELS[filters.period] || 'Custom';
    const today = new Date().toISOString().split('T')[0];
    XLSX.writeFile(wb, `PM_Timesheet_${periodLabel}_${today}.xlsx`);
  };

  return (
    <div className="pm-timesheet-page">
      <div className="page-header d-print-none mb-3">
        <div className="row align-items-center">
          <div className="col">
            <div className="page-pretitle">Project Management</div>
            <h2 className="page-title mb-1">
              <Clock size={22} className="me-2 text-muted" />
              IT Audit Timesheet
            </h2>
            <p className="text-muted mb-0">
              Review task work logs from Clock In, Clock Out, and Manual Work Log.
            </p>
          </div>
          <div className="col-auto ms-auto d-print-none">
            <div className="btn-list">
              <button
                className="btn btn-outline-secondary d-inline-flex align-items-center"
                type="button"
                onClick={handleExportExcel}
                disabled={isLoading || data.entries.length === 0}
                title="Export the currently filtered entries to Excel"
              >
                <Download size={16} className="me-2" />
                Export to Excel
              </button>
              <button className="btn btn-outline-primary d-inline-flex align-items-center" type="button" onClick={fetchTimesheet} disabled={isLoading}>
                <RefreshCw size={16} className={`me-2 ${isLoading ? 'pm-timesheet-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      {errorMessage && (
        <div className="alert alert-warning pm-timesheet-error d-flex align-items-center" role="alert">
          <AlertTriangle size={18} className="me-3 flex-shrink-0" />
          <div className="flex-fill">
            <div className="fw-semibold">Timesheet data unavailable</div>
            <div className="pm-timesheet-helper">{errorMessage}</div>
          </div>
          <button className="btn btn-sm btn-outline-warning ms-3" onClick={fetchTimesheet}>Retry</button>
        </div>
      )}

      <div className="card pm-timesheet-filter-card mb-3">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-12 col-lg-4">
              <label className="form-label" htmlFor="pm-timesheet-search">Search</label>
              <div className="input-icon">
                <span className="input-icon-addon">
                  <Search size={16} />
                </span>
                <input
                  id="pm-timesheet-search"
                  className="form-control"
                  placeholder="Search project, member, or task..."
                  value={filters.search}
                  onChange={(e) => setFilter('search', e.target.value)}
                />
              </div>
            </div>
            <div className="col-12 col-md-4 col-lg-2">
              <label className="form-label" htmlFor="pm-timesheet-member">Member</label>
              <select id="pm-timesheet-member" className="form-select" value={filters.userId} onChange={(e) => setFilter('userId', e.target.value)}>
                <option value="">All members</option>
                {members.map((member) => (
                  <option key={member.id} value={member.id}>{member.name || member.email || `User #${member.id}`}</option>
                ))}
              </select>
            </div>
            <div className="col-12 col-md-4 col-lg-2">
              <label className="form-label" htmlFor="pm-timesheet-period">Period</label>
              <select id="pm-timesheet-period" className="form-select" value={filters.period} onChange={(e) => setFilter('period', e.target.value)}>
                {periodOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
            </div>
            <div className="col-12 col-md-4 col-lg-2">
              <label className="form-label" htmlFor="pm-timesheet-source">Source</label>
              <select id="pm-timesheet-source" className="form-select" value={filters.source} onChange={(e) => setFilter('source', e.target.value)}>
                {sourceOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
            </div>
            <div className="col-12 col-lg-2">
              <button className="btn btn-outline-secondary w-100" type="button" onClick={clearFilters}>Clear</button>
            </div>
          </div>
        </div>
      </div>

      <div className="row g-3 mb-3">
        {summaryCards.map((card) => (
          <SummaryCard key={card.label} {...card} isLoading={isLoading} />
        ))}
      </div>

      <div className="card">
        <div className="card-header">
          <div>
            <h3 className="card-title mb-1">Timesheet Entries</h3>
            <div className="card-subtitle text-muted">
              {data.entries.length} work log{data.entries.length !== 1 ? 's' : ''} shown, {formatHours(totalFilteredHours)} total.
            </div>
          </div>
        </div>
        <div className="table-responsive">
          <table className="table table-vcenter card-table pm-timesheet-table mb-0">
            <thead>
              <tr>
                <th>Date</th>
                <th>Project</th>
                <th>Member</th>
                <th>Task / Subtask</th>
                <th>Start</th>
                <th>End</th>
                <th className="text-end">Duration</th>
                <th>Source</th>
                <th>Note</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={9}><span className="placeholder col-12" /></td></tr>
              ) : data.entries.length === 0 ? null : (
                data.entries.map((entry) => (
                  <tr key={entry.id}>
                    <td>{formatDate(entry.work_date || entry.started_at)}</td>
                    <td>
                      <div className="fw-semibold">{entry.project_title || '-'}</div>
                    </td>
                    <td>{entry.member_name || '-'}</td>
                    <td>
                      <div className="fw-semibold">{entry.task_title || '-'}</div>
                      {entry.task_type === 'subtask' && entry.parent_task_title && (
                        <div className="text-muted pm-timesheet-helper">Subtask of {entry.parent_task_title}</div>
                      )}
                    </td>
                    <td>{formatTime(entry.started_at)}</td>
                    <td>{entry.ended_at ? formatTime(entry.ended_at) : <span className="badge bg-warning-lt">Active</span>}</td>
                    <td className="text-end fw-semibold">{formatDuration(entry.duration_seconds)}</td>
                    <td><span className={`badge bg-${entry.source === 'manual' ? 'purple' : 'teal'}-lt text-capitalize`}>{entry.source || 'realtime'}</span></td>
                    <td className="pm-timesheet-note">{entry.description || '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {!isLoading && data.entries.length === 0 && <EmptyTimesheetState />}
      </div>
    </div>
  );
};

export default PmTimesheet;
